import { generateClient, get } from "aws-amplify/api";

/**
 * Umur Piutang & Hutang (aging) and Buku Besar Pembantu (partner ledger) on
 * the accounting API.
 *
 * Plain promise helpers, like deposit.js: each page keeps its own copy of the
 * report, so the shared accounting slice stays out of it. Both endpoints are
 * GET with a query string. Every helper resolves to the envelope's `data` and
 * throws the raw API error otherwise, for getErrorMessages to read.
 */

const API_NAME = "accounting";

const unwrap = async (request) => {
  const { body } = await request.response;
  const res = await body.json();
  if (res?.success === false) {
    const error = new Error(res.message || "Request failed");
    error.response = { body: res };
    throw error;
  }
  return res?.data;
};

const query = (params) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      search.set(key, value);
    }
  });
  return search.toString();
};

/**
 * `type` is "receivable" or "payable"; `date` the report date as an ISO
 * instant (end of the WIB day). With `exportType` ("pdf" / "xlsx") resolves to
 * `{ key, bucket, region }` of the exported file instead of the report.
 */
export const getAging = ({ businessID, type, date, exportType }) =>
  unwrap(
    get({
      apiName: API_NAME,
      path: `/aging?${query({
        business_id: businessID,
        type,
        date,
        export: exportType,
      })}`,
    }),
  );

/** One contact's movements between `startDate` and `endDate` (ISO instants). */
export const getPartnerLedger = ({
  businessID,
  partnerID,
  type,
  startDate,
  endDate,
  exportType,
}) =>
  unwrap(
    get({
      apiName: API_NAME,
      path: `/partner-ledger?${query({
        business_id: businessID,
        partner_id: partnerID,
        type,
        start_date: startDate,
        end_date: endDate,
        export: exportType,
      })}`,
    }),
  );

const client = generateClient();

const partnerNames = /* GraphQL */ `
  query PartnerNamesByBusiness(
    $businessID: ID
    $limit: Int
    $nextToken: String
  ) {
    partnersByBusinessCreatedAt(
      businessID: $businessID
      sortDirection: ASC
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        name
      }
      nextToken
    }
  }
`;

/**
 * Every contact of the business as `{ id, name }`, all pages. The shared
 * partner list stops after its first page, so a business with many contacts
 * would miss some in the ledger's picker.
 */
export const listAllPartners = async (businessID) => {
  const partners = [];
  let nextToken = null;
  do {
    // eslint-disable-next-line no-await-in-loop
    const res = await client.graphql({
      query: partnerNames,
      variables: { businessID, limit: 1000, nextToken },
    });
    const page = res.data.partnersByBusinessCreatedAt;
    partners.push(...page.items);
    nextToken = page.nextToken;
  } while (nextToken);
  return partners;
};
